import { useState, useEffect, useRef, useCallback } from "react";
import { ReactComponent as Logo } from "../../../../assets/icons/animation-logo.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/x-close.svg";
import { ReactComponent as MicIcon } from "../../../../assets/icons/mic.svg";
import { ReactComponent as SubmitIcon } from "../../../../assets/icons/submit.svg";
import { ReactComponent as RecordingIndicatorIcon } from "../../../../assets/icons/recording-indicator.svg";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../../../services/app-context";
import { SignedUrlImage } from "../../../../components/SignedUrlImage";
import { useSignedUrl } from "../../../../services/useSignedUrl";
import { api } from "../../../../services/api";
import { withBackoff } from "../../../../services/with-backoff";
import { ShowToast } from "../../../../services/toast";
import { getLoggerRef } from "../../../../integrations/logger/logger-bridge";
import { LottieLoading } from "../../../../components/Loading/LottieLoading";
import { CertificationLoading } from "./CertificationLoading";
import { CertificationSubmitLoading } from "./CertificationSubmitLoading";
import { CertificationSuccess } from "./CertificationSuccess";
import { CertificationContinuePrompt } from "./CertificationContinuePrompt";
import { useUser } from "../../../../services/user-context";
import AWS from "aws-sdk";
import { REGION } from "../../../../env";
import { getLabel, getUrl } from "../../utils/labels";
import {
  createMp3File,
  encodePcmToMp3Blob,
  float32ToInt16,
  getMp3PutObjectContentType,
} from "../utils/mp3-audio";

interface CertificationCallProps {
  onCountdownComplete?: () => void;
  certificationData?: any;
  showLoading?: boolean;
  onCertificationDataUpdate?: (data: any) => void;
  assignmentId: string;
  minimumAttempts?: number;
}

export function CertificationCall({
  onCountdownComplete,
  certificationData,
  showLoading = false,
  onCertificationDataUpdate,
  assignmentId,
  minimumAttempts = 1,
}: CertificationCallProps) {
  const navigate = useNavigate();
  const { tenantProfile, styles, certificationAssets } = useApp();
  const { userInfo, credentials } = useUser() as any;
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const labels = certificationAssets?.labels ?? {};
  const imagePath = certificationData?.image;
  const imagePathParts = imagePath ? imagePath.split("/") : [];
  const imageKey = imagePathParts[imagePathParts.length - 1] ?? "";
  const imagePathPrefix = imagePathParts.length > 1 ? imagePathParts.slice(0, -1).join("/") : "app/assets/images";
  // Gate layout on the signed URL being ready, not just the key. useSignedUrl
  // is also called inside SignedUrlImage — TanStack Query deduplicates so no
  // extra network request is made. This prevents the half-width empty gray
  // panel that would appear while the async URL fetch is in flight.
  const imageUrl = useSignedUrl(imageKey, undefined, imagePathPrefix);
  const [isRecording, setIsRecording] = useState(false);
  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const [canStopRecording, setCanStopRecording] = useState(false);
  const [isConvertingAudio, setIsConvertingAudio] = useState(false);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState<Blob | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showContinuePrompt, setShowContinuePrompt] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const isProcessingRef = useRef(false);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStartedAtRef = useRef<number | null>(null);
  const stopLockoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const routeNames = tenantProfile?.routeNames ?? {};
  const url = getUrl("certificationRoute", routeNames);

  const navigateToCertifications = useCallback(() => {
    navigate(`/${url}`);
  }, [navigate, url]);

  const handleClose = useCallback(() => {
    navigateToCertifications();
  }, [navigateToCertifications]);

  const handleSuccessComplete = useCallback(() => {
    const attemptNumber = certificationData?.attemptNumber ?? 1;
    if (attemptNumber < minimumAttempts) {
      setShowContinuePrompt(true);
    } else {
      navigateToCertifications();
    }
  }, [certificationData?.attemptNumber, minimumAttempts, navigateToCertifications]);

  const handleBeginNext = useCallback(() => {
    navigate(`/${url}/simulation?assignmentId=${assignmentId}&minimumAttempts=${minimumAttempts}&_r=${Date.now()}`);
  }, [navigate, url, assignmentId, minimumAttempts]);

  const convertToMp3 = async (webmBlob: Blob): Promise<Blob> => {
    const arrayBuffer = await webmBlob.arrayBuffer();
    const audioContext = new AudioContext();
    let audioBuffer: AudioBuffer;
    try {
      audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    } finally {
      audioContext.close();
    }

    const channels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const leftData = float32ToInt16(audioBuffer.getChannelData(0));
    const rightData = channels > 1 ? float32ToInt16(audioBuffer.getChannelData(1)) : leftData;

    return encodePcmToMp3Blob(leftData, rightData, channels, sampleRate);
  };

  const startRecording = async () => {
    setIsStartingRecording(true);
    try {
      const constraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      };
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err: any) {
        const isDeviceBusy =
          err?.name === "NotReadableError" ||
          (typeof err?.message === "string" && /busy|in use/i.test(err.message));
        if (isDeviceBusy) {
          await new Promise((r) => setTimeout(r, 1500));
          stream = await navigator.mediaDevices.getUserMedia(constraints);
        } else {
          throw err;
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        getLoggerRef()?.debug('[CertRecording] ondataavailable', { chunkSize: event.data.size, totalChunks: audioChunksRef.current.length + 1, recorderState: mediaRecorder.state });
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const totalChunks = audioChunksRef.current.length;
        const totalChunkBytes = audioChunksRef.current.reduce((sum, c) => sum + c.size, 0);
        const webmBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

        getLoggerRef()?.warn('[CertRecording] onstop', { totalChunks, totalChunkBytes, blobSize: webmBlob.size, blobType: webmBlob.type, hadTimesliceChunks: totalChunks > 1 });

        // Stop all tracks to release the microphone
        stream.getTracks().forEach(track => track.stop());

        // A valid recording has meaningful audio data beyond just the container header.
        // Blobs smaller than 5KB indicate a corrupted or empty recording.
        const MIN_VALID_SIZE_BYTES = 5 * 1024;
        if (webmBlob.size < MIN_VALID_SIZE_BYTES) {
          getLoggerRef()?.error('[CertRecording] Blob failed validation — likely corrupted or empty', undefined, { blobSize: webmBlob.size, totalChunks, totalChunkBytes });
          audioChunksRef.current = [];
          ShowToast({ message: 'Recording failed. Please try again.', type: 'error' });
          return;
        }

        getLoggerRef()?.info('[CertRecording] WebM blob valid, converting to MP3', { blobSize: webmBlob.size, totalChunks });
        setIsConvertingAudio(true);
        convertToMp3(webmBlob)
          .then((mp3Blob) => {
            getLoggerRef()?.info('[CertRecording] MP3 conversion complete', { mp3Size: mp3Blob.size });
            if (mp3Blob.size < MIN_VALID_SIZE_BYTES) {
              getLoggerRef()?.error('[CertRecording] MP3 blob failed validation — likely corrupted or empty', undefined, { mp3Size: mp3Blob.size, originalWebmSize: webmBlob.size });
              ShowToast({ message: 'Recording failed. Please try again.', type: 'error' });
              return;
            }
            setRecordedAudioBlob(mp3Blob);
          })
          .catch((error) => {
            getLoggerRef()?.error('[CertRecording] MP3 conversion failed', error, { blobSize: webmBlob.size });
            ShowToast({ message: 'Recording failed. Please try again.', type: 'error' });
          })
          .finally(() => {
            setIsConvertingAudio(false);
          });
      };

      mediaRecorder.start(1000);
      recordingStartedAtRef.current = Date.now();
      getLoggerRef()?.warn('[CertRecording] MediaRecorder started', { mimeType: mediaRecorder.mimeType, state: mediaRecorder.state });
      setIsRecording(true);
      stopLockoutTimerRef.current = setTimeout(() => setCanStopRecording(true), 2000);
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error starting recording:', error);
      ShowToast({ message: 'Unable to access microphone. Please check your permissions.', type: 'error' });
    } finally {
      setIsStartingRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      const durationMs = recordingStartedAtRef.current !== null ? Date.now() - recordingStartedAtRef.current : null;
      getLoggerRef()?.warn('[CertRecording] stopRecording called', { recorderState: mediaRecorderRef.current.state, chunksSoFar: audioChunksRef.current.length, durationMs });
      if (stopLockoutTimerRef.current !== null) {
        clearTimeout(stopLockoutTimerRef.current);
        stopLockoutTimerRef.current = null;
      }
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setCanStopRecording(false);
      recordingStartedAtRef.current = null;
    }
  };

  const handleRecordingToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const uploadAudioToS3 = async (audioBlob: Blob): Promise<string> => {
    if (!userInfo?.id || !assignmentId || !tenantProfile?.assets_bucket) {
      console.error('Missing data:', {
        userId: userInfo?.id,
        assignmentId: assignmentId,
        assetsBucket: tenantProfile?.assets_bucket,
        certificationData: certificationData
      });
      throw new Error('Missing required data for upload');
    }

    const s3 = new AWS.S3();
    
    if (credentials?.AccessKeyId && credentials?.SecretAccessKey) {
      AWS.config.update({
        accessKeyId: credentials.AccessKeyId,
        secretAccessKey: credentials.SecretAccessKey,
        region: REGION,
        signatureVersion: "v4",
        credentials: {
          accessKeyId: credentials.AccessKeyId,
          secretAccessKey: credentials.SecretAccessKey,
          sessionToken: credentials.SessionToken,
        }
      });
    }

    const fileName = `${certificationData.stepNumber}.mp3`;
    const path = `simulations/users/${userInfo.id}/assignments/${assignmentId}/${certificationData.attemptNumber}`;
    const mp3ContentType = getMp3PutObjectContentType();
    
    const params = {
      Bucket: tenantProfile.assets_bucket,
      Key: `${path}/${fileName}`,
      ContentType: mp3ContentType,
      Expires: 60 * 60 * 24 * 3,
      ServerSideEncryption: "aws:kms",
    };

    const audioFile = createMp3File(audioBlob, fileName);

    try {
      const signedUrl = await s3.getSignedUrlPromise("putObject", params);
      
      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        headers: {
          "Content-Type": mp3ContentType,
        },
        body: audioFile,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error("Upload failed:", uploadResponse.status, errorText);
        throw new Error(`Upload failed with status ${uploadResponse.status}: ${errorText}`);
      }

      console.log("Audio uploaded successfully to:", `${path}/${fileName}`);

      // Return just the path in the bucket
      const audioUrl = `${path}/${fileName}`;
      return audioUrl;
    } catch (error) {
      console.error("Error uploading audio:", error);
      throw error;
    }
  };

  const handleNextQuestion = async () => {
    if (!recordedAudioBlob || !certificationData) return;
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setIsProcessing(true);
    try {
      // Upload audio to S3
      const audioUrl = await uploadAudioToS3(recordedAudioBlob);

      // Call next question API
      const response = await api.nextCertificationQuestion(
        assignmentId,
        certificationData.attemptNumber,
        certificationData.stepNumber,
        audioUrl
      );

      // Update certification data with new question, preserving attemptNumber and certificationName from original data
      if (onCertificationDataUpdate && response.data) {
        onCertificationDataUpdate({
          ...response.data,
          attemptNumber: certificationData.attemptNumber, // Preserve attempt number from initial start call
          certificationName: certificationData.certificationName, // Preserve certification name from initial start call
        });
      }

      // Reset recording state for next question
      setRecordedAudioBlob(null);
      setIsProcessing(false);
    } catch (error) {
      console.error('Error moving to next question:', error);
      alert('Failed to submit answer. Please try again.');
      setIsProcessing(false);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const pollForAiReview = async (id: string, attemptNumber: number): Promise<void> => {
    const maxPendingRetries = 50;
    let pendingCount = 0;
    while (pendingCount < maxPendingRetries) {
      const response = await withBackoff(() => api.watchAttemptReview(id, attemptNumber));
      if (response?.data?.data === "ready") {
        return;
      }
      // status is "pending" — backend holds the connection, retry immediately
      pendingCount++;
    }
    // max pending retries reached — proceed anyway so the user is not stuck
  };

  const handleSubmitCertification = async () => {
    if (!recordedAudioBlob || !certificationData) return;
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setIsProcessing(true);
    setIsSubmitting(true);

    try {
      // Upload audio to S3 and call API
      const audioUrl = await uploadAudioToS3(recordedAudioBlob);

      await api.endCertification(
        assignmentId,
        certificationData.attemptNumber,
        certificationData.stepNumber,
        audioUrl
      );
    } catch (error) {
      console.error('Error submitting certification:', error);
      alert('Failed to submit certification. Please try again.');
      setIsProcessing(false);
      setIsSubmitting(false);
      isProcessingRef.current = false;
      return;
    }

    // Submission succeeded — keep loading screen visible until AI review is ready.
    // Polling errors here don't affect the submission itself, so proceed to success regardless.
    try {
      await pollForAiReview(assignmentId, certificationData.attemptNumber);
    } catch (error) {
      console.error('Error polling for AI review:', error);
    }

    isProcessingRef.current = false;
    setIsProcessing(false);
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  // Play audio when question loads
  useEffect(() => {
    const audioUrl = certificationData?.audioUrl;
    
    if (audioUrl && !showLoading) {
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      // Set up audio event listeners
      audio.onplay = () => setIsAudioPlaying(true);
      audio.onpause = () => setIsAudioPlaying(false);
      audio.onended = () => setIsAudioPlaying(false);
      
      audio.play().catch((error) => {
        console.error("Failed to play audio:", error);
        setIsAudioPlaying(false);
      });

      // Cleanup function
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        setIsAudioPlaying(false);
      };
    }
  }, [certificationData?.audioUrl, showLoading]);

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (stopLockoutTimerRef.current !== null) {
        clearTimeout(stopLockoutTimerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        getLoggerRef()?.warn('[CertRecording] MediaRecorder stopped during unmount — possible race condition', { recorderState: mediaRecorderRef.current.state, chunksSoFar: audioChunksRef.current.length });
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // Log when audio is recorded
  useEffect(() => {
    if (recordedAudioBlob) {
      console.log('Audio recorded successfully!', {
        size: `${(recordedAudioBlob.size / 1024).toFixed(2)} KB`,
        type: recordedAudioBlob.type,
        blob: recordedAudioBlob
      });
      
      // You can also create a download URL for testing
      const url = URL.createObjectURL(recordedAudioBlob);
      console.log('Audio URL (for testing):', url);
    }
  }, [recordedAudioBlob]);

  // Show loading overlay if needed
  if (showLoading) {
    return (
      <CertificationLoading 
        countdownFrom={3}
        onCountdownComplete={onCountdownComplete}
      />
    );
  }

  // Show submission loading screen
  if (isSubmitting) {
    return <CertificationSubmitLoading />;
  }

  // Show continue prompt if user hasn't met minimum attempts
  if (showContinuePrompt) {
    return (
      <CertificationContinuePrompt
        completedAttemptNumber={certificationData?.attemptNumber ?? 1}
        minimumAttempts={minimumAttempts}
        onStartLater={navigateToCertifications}
        onBeginNext={handleBeginNext}
      />
    );
  }

  // Show success screen after submission
  if (isSubmitted) {
    return <CertificationSuccess onComplete={handleSuccessComplete} />;
  }

  return (
    <div className="fixed inset-0 bg-background flex">
      {/* Main Content Area - shifts to left when image is shown */}
      <div className={`flex flex-col transition-all duration-300 ${imageKey ? 'w-1/2' : 'w-full'}`}>
        {/* Header Section - same as intro */}
        <div className="p-6 flex justify-between items-start gap-8 relative z-10">
          <div className="flex flex-col gap-2 flex-1">
            <h1 className="text-body font-pp-semibold text-text-primary">{getLabel("certification.singular", labels)}</h1>
            <p className="text-caption1 font-pp-medium text-text2">
              {getLabel("session.singular", labels)}: {certificationData?.certificationName || "Loading..."}
            </p>
            <p className="text-caption1 font-pp-medium text-text2">
              {getLabel("attempt.singular", labels)} #: {certificationData?.attemptNumber || 1}{certificationData?.maxAttempts ? `/${certificationData.maxAttempts}` : ''}
            </p>
          </div>

          {/* Close Button */}
          <div className="flex items-center gap-3 flex-shrink-0 relative">
          <button 
            onClick={handleClose}
            className="w-12 h-12 bg-white border border-gray-300 rounded-full shadow-sm flex items-center justify-center hover:bg-gray-50 transition-all"
          >
            <CloseIcon className="w-5 h-5 [&_path]:stroke-gray-400" />
          </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center gap-12 px-4">
        {/* Question Header */}
        <div className="text-center space-y-4">
          <h2 className="text-subheadline font-pp-medium text-text-primary">
            {getLabel("question.singular", labels)} {certificationData?.stepNumber || 1}{certificationData?.totalSteps ? ` of ${certificationData.totalSteps}` : ''}
          </h2>
          <p className="text-small font-pp-medium text-text2 max-w-2xl">
            {certificationData?.question || `Loading ${getLabel("question.singular", labels, false)}...`}
          </p>
          {certificationData?.subtext && certificationData.subtext.trim() !== '' && (
            <p className="text-caption1 font-pp-regular text-text2 max-w-2xl opacity-75 italic">
              {certificationData.subtext}
            </p>
          )}
        </div>

        {/* Logo Circle with audio/recording animation */}
        <div className="relative w-64 h-64 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center shadow-lg">
          <div 
            className={`w-56 h-56 rounded-full bg-white flex items-center justify-center transition-all duration-200 certification-logo-circle ${
              isRecording ? 'recording' : isAudioPlaying ? 'playing' : ''
            }`}
          >
            {styles?.logo ? (
              <SignedUrlImage
                s3Key={styles?.smallLogo}
                bucket={tenantProfile?.assets_bucket}
                alt="logo"
                className="w-32 h-32 object-contain"
              />
            ) : (
              <Logo className="w-32 h-32" />
            )}
          </div>
        </div>

        {/* Record/Submit Button */}
        <div className="relative">
          {isConvertingAudio ? (
            <div className="flex items-center justify-center gap-2 h-[51px] px-4">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin"></div>
              <span className="text-small font-pp-regular text-gray-800">Processing recording...</span>
            </div>
          ) : !recordedAudioBlob ? (
            // Recording state - show record or stop recording button
            <>
              <button
                onClick={handleRecordingToggle}
                disabled={isStartingRecording || (isRecording && !canStopRecording)}
                className={`flex items-center justify-center gap-2 h-[51px] px-4 rounded-full shadow-md transition-all ${
                  isRecording
                    ? 'bg-red-50 hover:bg-red-100 border-0'
                    : 'bg-white border border-gray-300 hover:bg-gray-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isRecording ? (
                  <RecordingIndicatorIcon className="w-[18px] h-[18px] flex-shrink-0" aria-hidden />
                ) : (
                  <MicIcon className="w-12 h-12 [&_path]:fill-gray-700" />
                )}
                <span className="text-small font-pp-regular text-gray-800">
                  {isRecording ? (canStopRecording ? 'Stop recording' : 'Recording...') : 'Record your answer'}
                </span>
              </button>
            </>
          ) : (
            // After recording - show next question or submit certification button
            certificationData?.hasNext ? (
              <button 
                onClick={handleNextQuestion}
                disabled={isProcessing}
                className={`flex items-center justify-center gap-2 h-[51px] px-4 bg-white border border-gray-300 rounded-full shadow-md transition-all ${
                  isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                }`}
              >
                {isProcessing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin"></div>
                    <span className="text-small font-pp-regular text-gray-800">Processing...</span>
                  </>
                ) : (
                  <span className="text-small font-pp-regular text-gray-800">Next {getLabel("question.singular", labels, false)}</span>
                )}
              </button>
            ) : (
              <button 
                onClick={handleSubmitCertification}
                disabled={isProcessing}
                className={`flex items-center justify-center gap-2 h-[51px] px-4 bg-white border border-gray-300 rounded-full shadow-md transition-all ${
                  isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                }`}
              >
                {isProcessing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin"></div>
                    <span className="text-small font-pp-regular text-gray-800">Submitting...</span>
                  </>
                ) : (
                  <>
                    <SubmitIcon className="w-6 h-6" />
                    <span className="text-small font-pp-regular text-gray-800">Submit {getLabel("certification.singular", labels)}</span>
                  </>
                )}
              </button>
            )
          )}
        </div>
        </div>
      </div>

      {/* Image Viewer - takes up right half of screen */}
      {imageKey && (
        <div className="fixed top-0 right-0 w-1/2 h-full z-[100] bg-white shadow-2xl flex flex-col">
          {/* Image Content */}
          <div className="flex-1 overflow-hidden bg-gray-200 relative flex items-center justify-center">
            {imageUrl ? (
              <SignedUrlImage
                s3Key={imageKey}
                bucket={tenantProfile?.assets_bucket}
                path={imagePathPrefix}
                alt="Question reference"
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <div className="absolute inset-0 flex justify-center items-center bg-white z-10">
                <LottieLoading />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

